import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../hooks/use-app';

interface SignaturePreviewItemProps {
  mediaID: string;
  thumbnailURL: string;
}

const closeButtonSx = {
  position: 'absolute', top: 4, right: 4, zIndex: 1,
};

/**
 * Self-contained signature thumbnail + preview modal.
 *
 * Signatures don't have MediaItemValue instances, so they can't use the
 * shared ModalMediaViewer pipeline (which relies on instanceof checks in
 * displayableItemForItem / metadataLoaderForItem). Instead, this component
 * replicates the legacy SignatureElementView viewer: click the thumbnail
 * to open a simple modal showing the full-resolution signature image.
 */
const SignaturePreviewItem: React.FC<SignaturePreviewItemProps> = ({ mediaID, thumbnailURL }) => {
  const [viewing, setViewing] = useState(false);
  const ctx = useApp();

  const previewURL = ctx.dataSource?.source?.getSignaturePreviewURL(mediaID) ?? '';

  const handleOpen = useCallback(() => {
    setViewing(true);
  }, []);

  const handleClose = useCallback(() => {
    setViewing(false);
  }, []);

  return (
    <>
      <Box
        component="img"
        src={thumbnailURL}
        loading="lazy"
        onClick={handleOpen}
        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '5px', cursor: 'pointer' }}
      />

      <Dialog
        open={viewing}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={handleClose}
            sx={closeButtonSx}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={previewURL}
            sx={{ width: '100%', display: 'block' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SignaturePreviewItem;
